import React, { useState } from 'react';
import { AlertTriangle, Trash2, CheckCircle } from 'lucide-react';
import { BackButton } from '../components/common';

const DeleteAccountPage = () => {
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate phone
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/account/deletion-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: `+91${cleanPhone}`,
          reason: reason || 'No reason provided',
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setError(data.detail || 'Failed to submit request. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Request Submitted
          </h1>
          <p className="text-gray-600 mb-6">
            Your account deletion request has been received. Our team will process your request within <strong>7 business days</strong>.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            You will receive an SMS confirmation once your account and associated data have been deleted.
          </p>
          <a
            href="/"
            className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-amber-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center space-x-3">
            <img
              src="/logo192.png"
              alt="Mint a Milli"
              className="w-10 h-10 rounded-lg"
            />
            <span className="font-serif text-xl font-bold text-amber-600">
              mint a milli
            </span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Warning Banner */}
          <div className="bg-red-50 border-b border-red-100 p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-red-800 mb-1">
                  Account Deletion Request
                </h2>
                <p className="text-red-700 text-sm">
                  This action is <strong>permanent</strong>. Your account access will be removed and personal information will be cleared from your profile.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Delete Your Account
              </h1>
              <p className="text-gray-600">
                Please enter the phone number associated with your Mint a Milli account.
              </p>
            </div>

            {/* Phone Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-4 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  +91
                </span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit number"
                  className="flex-1 block w-full rounded-r-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  required
                />
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for deletion (optional)
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
              >
                <option value="">Select a reason</option>
                <option value="No longer using the app">No longer using the app</option>
                <option value="Privacy concerns">Privacy concerns</option>
                <option value="Found a better alternative">Found a better alternative</option>
                <option value="Too many notifications">Too many notifications</option>
                <option value="Technical issues">Technical issues</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* What will be deleted */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">
                What will happen:
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <Trash2 className="w-4 h-4 text-red-500 mr-2" />
                  Your profile information (name, phone) will be cleared
                </li>
                <li className="flex items-center">
                  <Trash2 className="w-4 h-4 text-red-500 mr-2" />
                  Bank account details will be removed
                </li>
                <li className="flex items-center">
                  <Trash2 className="w-4 h-4 text-red-500 mr-2" />
                  You will no longer be able to access this account
                </li>
                <li className="flex items-center">
                  <Trash2 className="w-4 h-4 text-red-500 mr-2" />
                  Active vouchers will be forfeited
                </li>
              </ul>
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> As per RBI and PML Act guidelines, transaction records and KYC information are retained for regulatory compliance and audit purposes.
                </p>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || phone.length !== 10}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5 mr-2" />
                  Submit Deletion Request
                </>
              )}
            </button>

            {/* Note */}
            <p className="text-xs text-gray-500 text-center">
              By submitting this request, you acknowledge that your account access will be permanently removed. Account deletion requires zero Milli balance and no pending transactions.
            </p>
          </form>
        </div>

        {/* Contact Support */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">
            Have questions or need assistance?
          </p>
          <a
            href="mailto:support@mothersgold.in"
            className="text-amber-600 hover:text-amber-700 font-medium"
          >
            Contact Support: support@mothersgold.in
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} Mothers Gold & Diamonds LLP. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DeleteAccountPage;
